import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Nav, NavItem, NavLink, Row } from 'reactstrap';
import VideoSnapshot from 'video-snapshot';
import uuid from 'react-uuid';
import { useNavigate } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { ADD_PHOTO_START, ADD_VIDEO_START } from 'addvehicle/AddVehicleReducer';
import ConfirmNumberForm from './ConfirmNumberForm';
import errorIcon from '../../assets/img/icons/errorIcon.svg';
import { apiClient } from '../../api/ApiClient';
import AddNumberForm from './AddNumberForm';
import AddDescriptionPage from '../adddescription/AddDescriptionPage';
import ServiceHistoryPage from '../servicehistory/ServiceHistoryPage';
import AddFeaturesForm from './AddFeaturesForm';
import AddPhotoForm from './AddPhotoForm';
import { AddVehicleNavStructure, AddVehicleSteps } from '../../constants/AddVehicle';
import AddBasicCarDataForm from './AddBasicCarDataForm';
import AddVideoForm from './AddVideoForm';
import TyreTreadDepths from './TyreTreadDepths';
import LaunchVehicle from './LaunchVehicle';
import ConditionInspectionForm from '../condition/ConditionInspectionForm';
import { dataToSelect } from '../../common/helpers/dataToSelect';
import { UploadFileTypes } from '../../constants/UploadFileTypes';
import { VehicleApiEndpoints } from '../../constants/ApiEndpoints';
import {
  isValidVehicleData,
  mapVehicleDataToForm,
} from '../../common/utils/vehicleDataMapper';

// import { showAlert } from '../../common/components/alert/AlertActions';
// import { usePrompt } from '../../common/hooks/usePrompt';
import {
  handleCarDataAutofil,
  handleUploadPhotoError,
  removePhotosSoft,
  saveAuction,
  saveAuctionDraft,
  saveDescriptionToOrganisation,
  saveDraft,
  sendPaleteNumber,
  sendVehiclePhoto,
  updateSectionValidation,
  uploadVehicleVideo,
} from '../AddVehicleActions';
import {
  validateLaunchData,
  validateServiceHistory as validateServiceHistoryLocal,
  validateVehicleBasicData,
  validateVehicleDescription,
  validateVehicleFeatures as validateVehicleFeaturesLocal,
  validateVehiclePhotos,
  validateVehicleRegistrationPlate,
  validateVehicleTyres,
} from '../helpers/draftValidator';
import { resizeImage } from '../../common/utils/image/Image.utils';
import { getPhotoDescription } from '../../common/helpers/getPhotoDescription';
import RouterPaths from '../../constants/RouterPaths';
import NavGroup from '../../common/components/NavGroup';

const prepareThumbnailFile = async (mediaFile) => {
  const res = await fetch(mediaFile);
  const blob = await res.blob();
  const file = new File([blob], 'thumbnail', { type: 'image/jpeg', lastModified: Date.now() });
  const formData = new FormData();
  formData.append('file', file);
  return formData;
};

function flattenSelectField(field) {
  if (field && typeof field.value === 'object' && field.value.value) {
    return field.value;
  }
  return field;
}

const AddVehicleForm = React.memo(
  ({
    form,
    setSavingDraft,
    setPublishing,
    saveDraftRef,
    publishRef,
    setIsScheduledAuction,
    isScheduledAuction,
    isPublishing,
  }) => {
    const [publishAttempted, setPublishAttempted] = useState(false);

    const { values } = form;
    const vehicleMediaData = useMemo(
      () => form.values.vehicleMedia,
      [form.values.vehicleMedia],
    );
    const { videos } = vehicleMediaData;
    const {
      autofill,
      vehicleFound,
      reduxVehicleMedia,
      reduxDamages,
      reduxBasicData,
      reduxPhotosToDelete,
    } = useSelector((state) => {
      const addVehicleState = state.addVehicle.toJS();
      return {
        autofill: addVehicleState.autofill,
        vehicleFound: addVehicleState.vehicleFound,
        vehicleMedia: addVehicleState.vehicleMedia,
        reduxVehicleMedia: addVehicleState.vehicleMedia,
        reduxDamages: addVehicleState.damages,
        reduxBasicData: addVehicleState.basicData,
        reduxPhotosToDelete: addVehicleState.photosToDelete,
      };
    }, isEqual);

    const [makeOptions, setMakeOptions] = useState([]);
    const [modelOptions, setModelOptions] = useState([]);
    const [isVideoLoading, setVideoLoading] = useState(false);
    const [validationError, setValidationError] = useState(false);
    const [isRegistrationLoading, setRegistrationLoading] = useState(false);

    const scrollToSection = useCallback((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        const navbarOffsetPx = 100;
        const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
        const targetScrollTop = Math.max(elementTop - navbarOffsetPx, 0);
        window.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
      }
    }, []);
    // Connect to Redux validation errors and map server-side errors to sections
    const rawValidationErrors = useSelector((state) => {
      const errors = state.addVehicle.get('validationErrors');
      return errors && errors.toJS ? errors.toJS() : errors;
    });
    // Improved: Map server-side errors from All to sections for nav error icons, handling dot notation keys
    const mapServerErrorsToSections = (errors) => {
      // **MODIFICATION 1:** Remove the check for `!errors.All`
      if (!errors || Object.keys(errors).length === 0) return {};

      const sectionMap = {
        basicData: 'VEHICLE_DETAILS',
        vehicleFeatures: 'SPECIFICATION',
        descriptionAndSettings: 'DESCRIPTION',
        vehicleMedia: 'PHOTOS',
        vehicleTyres: 'TYRES',
        tyreThreadDepths: 'TYRES',
        serviceHistory: 'SERVICE_HISTORY',
        launchData: 'LAUNCH_VEHICLE',
      };

      const launchFieldSubKeys = [
        'locationId',
        'auctionEndDate',
        'startingPrice',
        'reservePrice',
        'autoAcceptPriceInPence',
      ];

      const mapped = {};

      // **MODIFICATION 2:** Iterate over `errors` directly, not `errors.All`
      Object.entries(errors).forEach(([key, value]) => {
        // This part of the logic remains the same and is still correct
        const topKey = key.split('.')[0];
        const subKey = key.split('.')[1];

        if (topKey === 'descriptionAndSettings' && launchFieldSubKeys.includes(subKey)) {
          mapped.LAUNCH_VEHICLE = mapped.LAUNCH_VEHICLE || {};
          mapped.LAUNCH_VEHICLE[key] = value;
          return;
        }

        const section = sectionMap[topKey] || sectionMap[key];
        if (section) {
          mapped[section] = mapped[section] || {};
          mapped[section][key] = value;
        }
      });

      return mapped;
    };
    const serverSectionErrors = mapServerErrorsToSections(rawValidationErrors);
    const validationErrors = useMemo(() => {
      // If serverSectionErrors has content, it's the most current validation state.
      // We should use it as the single source of truth to avoid data duplication.
      if (Object.keys(serverSectionErrors).length > 0) {
        return serverSectionErrors;
      }

      // If there are no server errors, fall back to the raw state,
      // which may contain client-side validation errors.
      return rawValidationErrors || {};
    }, [rawValidationErrors, serverSectionErrors]);
    useEffect(() => {
      // Only run this logic if the user has attempted to publish.
      if (!publishAttempted || !validationErrors) return;

      const sectionOrder = Object.keys(AddVehicleNavStructure).flatMap((groupKey) =>
        Object.keys(AddVehicleNavStructure[groupKey].children),
      );
      const firstErrorSection = sectionOrder.find(
        (sectionKey) =>
          validationErrors[sectionKey] && Object.keys(validationErrors[sectionKey]).length > 0,
      );
      if (firstErrorSection) {
        scrollToSection(firstErrorSection);
      }
      // Add publishAttempted to the dependency array
    }, [validationErrors, scrollToSection, publishAttempted]);

    const sectionRefs = useRef({});
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const showSuccessAlertRef = useRef(true);
    const hasAutofilledRef = useRef(false);

    const organizationLocations = useSelector((state) =>
      state.addVehicle.get('organizationLocations'),
    );
    const organizationGroup = useSelector((state) =>
      state.addVehicle.get('organizationGroup'),
    );
    const allVehicleFeatures = useSelector((state) =>
      state.addVehicle.get('allVehicleFeatures'),
    );

    useEffect(() => {
      apiClient.get(VehicleApiEndpoints.VEHICLE_MAKES).then((res) => {
        const options = dataToSelect(res?.data?.data, ['id', 'name']);
        setMakeOptions(options);
      });
    }, []);

    useEffect(() => {
      if (
        autofill === true &&
        reduxBasicData &&
        vehicleFound === true &&
        !hasAutofilledRef.current
      ) {
        hasAutofilledRef.current = true;

        if (isValidVehicleData(reduxBasicData)) {
          const mappedData = mapVehicleDataToForm(reduxBasicData);
          const selectFields = ['fuel', 'transmission', 'colour', 'carType'];
          Object.entries(mappedData).forEach(([fieldName, fieldValue]) => {
            if (selectFields.includes(fieldName)) {
              form.setFieldValue(`basicData.${fieldName}`, flattenSelectField(fieldValue));
            } else {
              form.setFieldValue(`basicData.${fieldName}`, fieldValue);
            }
          });

          // Fetch and set model options for autofilled make
          if (reduxBasicData.makeId) {
            apiClient
              .get(`${VehicleApiEndpoints.VEHICLE_MODELS}/${reduxBasicData.makeId}`)
              .then((modelList) => {
                const options = dataToSelect(modelList?.data?.data, ['id', 'name']);
                setModelOptions(options);
              });
          }
        }
      }

      if (autofill !== true || vehicleFound !== true) {
        hasAutofilledRef.current = false;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autofill, reduxBasicData, vehicleFound]);

    const handleSaveDraft = useCallback(
      async (opts) => {
        const skipNavigate = opts?.skipNavigate === true;
        setSavingDraft(true);
        let savedDraftId = null;
        try {
          const defaultDescription = values?.descriptionAndSettings?.defaultDescription || '';
          await saveDescriptionToOrganisation(defaultDescription || '');

          await dispatch(
            saveDraft(
              values,
              (newDraftId) => {
                savedDraftId = newDraftId;
                if (
                  !skipNavigate &&
                  newDraftId &&
                  !window.location.pathname.includes(newDraftId)
                ) {
                  navigate(`/add-vehicle/${newDraftId}`, { replace: true });
                }
              },
              showSuccessAlertRef?.current,
            ),
          );
        } catch (error) {
          console.error('Error saving draft:', error);
        } finally {
          setSavingDraft(false);
        }
        return savedDraftId;
      },
      [setSavingDraft, dispatch, values, navigate, showSuccessAlertRef],
    );

    const handlePublish = useCallback(async () => {
      setPublishAttempted(true);
      setPublishing(true);
      try {
        const defaultDescription = values?.descriptionAndSettings?.defaultDescription || '';
        await saveDescriptionToOrganisation(defaultDescription || '');
        const actionToUse = isScheduledAuction ? saveAuctionDraft : saveAuction;
        await dispatch(
          actionToUse(values, () => {
            navigate(RouterPaths.MY_VEHICLES);
          }),
        );
      } finally {
        setPublishing(false);
        setPublishAttempted(false);
      }
    }, [dispatch, values, setPublishing, navigate, isScheduledAuction]);

    useEffect(() => {
      if (saveDraftRef) {
        saveDraftRef.current = handleSaveDraft;
      }
    }, [saveDraftRef, handleSaveDraft]);

    useEffect(() => {
      if (publishRef) {
        publishRef.current = handlePublish;
      }
    }, [publishRef, handlePublish]);

    const handleMakeChange = useCallback(
      (e) => {
        form.setFieldValue('basicData.modelSelect', { value: '', label: '' });
        apiClient.get(`${VehicleApiEndpoints.VEHICLE_MODELS}/${e.value}`).then((modelList) => {
          const options = dataToSelect(modelList?.data?.data, ['id', 'name']);
          setModelOptions(options);
        });
      },
      [form],
    );
    const handleRegistrationSearch = useCallback(() => {
      const { registrationPlate } = form.values.basicData;
      if (!registrationPlate) {
        form.setFieldTouched('basicData.registrationPlate', true, true);
        return;
      }

      Object.keys(form.values.basicData).forEach((key) => {
        if (key !== 'registrationPlate') {
          form.setFieldValue(`basicData.${key}`, null);
        }
      });

      dispatch(handleCarDataAutofil(null));

      setRegistrationLoading(true);
      dispatch(
        sendPaleteNumber(registrationPlate, () => {
          setRegistrationLoading(false);
        }),
      );
    }, [form, dispatch]);

    const handleNoAutoComplete = useCallback(() => {
      Object.keys(form.values.basicData).forEach((key) => {
        if (key !== 'registrationPlate') {
          form.setFieldValue(`basicData.${key}`, null);
        }
      });

      dispatch(handleCarDataAutofil(false));
    }, [dispatch, form]);

    const handleTryAgain = useCallback(() => {
      Object.keys(form.values.basicData).forEach((key) => {
        form.setFieldValue(`basicData.${key}`, null);
      });

      form.setFieldValue('basicData.registrationPlate', '');
      dispatch({ type: 'VEHICLE_FOUND_SUCCESS', payload: null });
    }, [form, dispatch]);

    const handleRemoveVideoClick = useCallback(
      (id) => {
        const newVideos = videos.filter((video) => video.mediaMetadataId !== id);
        form.setFieldValue('vehicleMedia.videos', newVideos);
      },
      [form, videos],
    );

    const handlePhotoReorder = (reorderedPhotos) => {
      form.setFieldValue('vehicleMedia.photos', reorderedPhotos);
      dispatch({ type: 'ADD_PHOTO_START', payload: { photos: reorderedPhotos } });
    };

    const getThumbnail = useCallback(async (file) => {
      const snapshoter = new VideoSnapshot(file);
      const snap = await snapshoter.takeSnapshot();
      const formData = await prepareThumbnailFile(snap);
      return { previewDataUrl: snap, formData };
    }, []);

    const handleUploadVideo = useCallback(
      async (file) => {
        if (!file || !file.type.includes(UploadFileTypes.VIDEO)) {
          setValidationError(true);
          return;
        }
        setVideoLoading(true);
        setValidationError(false);
        try {
          const thumbnail = await getThumbnail(file);
          const pendingVideo = {
            fileUrl: URL.createObjectURL(file),
            previewUrl: thumbnail.previewDataUrl,
            status: 'PENDING',
            mediaMetadataId: `temp_id_${Date.now()}`,
          };

          const currentVideos = [...(reduxVehicleMedia?.videos || [])];
          const newVideos = [...currentVideos, pendingVideo];
          const currentIndex = newVideos.length - 1;

          dispatch({ type: ADD_VIDEO_START, payload: { videos: newVideos } });
          dispatch(uploadVehicleVideo(file, thumbnail, currentIndex));
        } catch (error) {
          console.error('Error processing video:', error);
          setValidationError(true);
        } finally {
          setVideoLoading(false);
        }
      },
      [getThumbnail, reduxVehicleMedia, dispatch],
    );

    const validatePhotos = useCallback(
      (valuesToValidate) => {
        const errors = validateVehiclePhotos(valuesToValidate);
        dispatch({
          type: 'ADD_DRAFT_VALIDATION_ERRORS',
          payload: [{ formStep: 'PHOTOS', errors }],
        });
      },
      [dispatch],
    );

    const handlePhotoDrop = useCallback(
      async (startIndex, files) => {
        if (!files || files.length === 0) {
          dispatch(handleUploadPhotoError(true));
          return;
        }
        dispatch(handleUploadPhotoError(false));
        const resizedImages = await Promise.all(files.map((f) => resizeImage(f)));
        const currentPhotos = [...(reduxVehicleMedia?.photos || [])];

        resizedImages.forEach((resizedImage, i) => {
          const idx = startIndex + i;
          const newPhotoData = {
            file: resizedImage,
            fileUrl: URL.createObjectURL(resizedImage),
            status: 'PENDING',
            perspective: getPhotoDescription(idx + 1).PERSPECTIVE,
          };
          while (currentPhotos.length <= idx) {
            currentPhotos.push({ status: 'EMPTY' });
          }
          currentPhotos[idx] = newPhotoData;
        });

        dispatch({ type: ADD_PHOTO_START, payload: { photos: currentPhotos } });

        resizedImages
          .map((resizedImage, i) => ({ file: resizedImage, idx: startIndex + i }))
          .forEach(({ file, idx }) => {
            dispatch(sendVehiclePhoto(file, idx));
          });
      },
      [dispatch, reduxVehicleMedia],
    );

    const handlePhotoDelete = useCallback(
      (photoIndex) => {
        const currentPhotos = [...(reduxVehicleMedia?.photos || [])];
        const updatedPhotos = currentPhotos.filter((_, index) => index !== photoIndex);
        dispatch(
          removePhotosSoft(photoIndex, currentPhotos, reduxVehicleMedia, reduxPhotosToDelete),
        );
        dispatch({ type: 'DELETE_PHOTO_START', payload: { photos: updatedPhotos } });

        validatePhotos(updatedPhotos);
      },
      [dispatch, reduxVehicleMedia, validatePhotos, reduxPhotosToDelete],
    );

    const navOptions = useMemo(
      () =>
        Object.values(AddVehicleNavStructure).map((group) => {
          const groupHasError = Object.keys(group.children).some(
            (childKey) => !isEmpty(validationErrors[childKey]),
          );
          return (
            <NavGroup
              key={group.label}
              title={group.label}
              hasError={groupHasError}
              defaultOpen={Object.values(AddVehicleNavStructure)[0] === group}
            >
              {Object.entries(group.children).map(([sectionKey, sectionLabel]) => {
                const sectionHasError = !isEmpty(validationErrors[sectionKey]);
                return (
                  <NavLink
                    key={sectionKey}
                    className="add-vehicle-nav-link"
                    onClick={() => scrollToSection(sectionKey)}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <span>{sectionLabel}</span>
                      {sectionHasError && (
                        <img
                          src={errorIcon}
                          alt="error"
                          className="ml-auto "
                          height={20}
                          style={{ paddingRight: '35px' }}
                        />
                      )}
                    </div>
                  </NavLink>
                );
              })}
            </NavGroup>
          );
        }),
      [validationErrors, scrollToSection],
    );

    const createRefCallback = useCallback(
      (sectionKey) => (el) => {
        sectionRefs.current[sectionKey] = el;
      },
      [],
    );

    const handleAutofill = useCallback(
      (accepted) => {
        dispatch(handleCarDataAutofil(accepted));
      },
      [dispatch],
    );

    const handleValidateSection = useCallback(
      (validator, sectionKey) => {
        if (isPublishing) {
          return; // Do not run local validation while publishing
        }
        const valuesToValidate = {
          ...form.values,
          vehicleMedia: reduxVehicleMedia || form.values.vehicleMedia,
          vehicleDamages: {
            damages: reduxDamages || form.values.vehicleDamages?.damages || [],
          },
        };
        const errors = validator(valuesToValidate);
        dispatch(updateSectionValidation(sectionKey, errors));
      },
      [isPublishing, form.values, reduxVehicleMedia, reduxDamages, dispatch],
    );
    return (
      <div className="add-vehicle-form-wrapper">
        <Row className="d-flex flex-column mt-5 add-vehicle-form-nav">
          <Col md={12} className="h-100 w-100 p-0">
            <Nav
              vertical
              className="settings-nav-container addVehicleNavGroup account-layout__nav d-flex flex-column flex-grow-1"
            >
              {navOptions.map((item) => {
                const navLink = React.Children.toArray(item.props.children)[0];
                const className = navLink?.props?.className || '';
                return (
                  <NavItem
                    key={item.key || uuid()}
                    className={className.includes('disabled') ? 'completed' : ''}
                  >
                    {item}
                  </NavItem>
                );
              })}
            </Nav>
          </Col>
        </Row>
        <div className="add-vehicle-form">
          <div
            id={AddVehicleSteps.VEHICLE_REGISTRATION}
            ref={createRefCallback(AddVehicleSteps.VEHICLE_REGISTRATION)}
            className="mt-4"
          >
            <AddNumberForm
              values={form.values}
              form={form}
              vehicleFound={vehicleFound}
              onSearch={handleRegistrationSearch}
              onNoAutoComplete={handleNoAutoComplete}
              onTryAgain={handleTryAgain}
              isLoading={isRegistrationLoading}
              autofill={autofill}
              onValidate={() =>
                handleValidateSection(validateVehicleRegistrationPlate, 'VEHICLE_REGISTRATION')
              }
              validationErrors={validationErrors}
            />
            {vehicleFound === true && autofill === null && (
              <div className="confirm-number-wrapper">
                <ConfirmNumberForm
                  vehicleData={{
                    makeName: reduxBasicData?.makeName || '',
                    modelName: reduxBasicData?.modelName || '',
                    fullName:
                      `${reduxBasicData?.makeName || ''} ${reduxBasicData?.modelName || ''}`.trim(),
                  }}
                  onAutofill={handleAutofill}
                />
              </div>
            )}
          </div>
          <fieldset
            disabled={vehicleFound === null || (vehicleFound === false && autofill !== false)}
            className={
              vehicleFound === null || (vehicleFound === false && autofill !== false)
                ? 'form-section-disabled'
                : ''
            }
          >
            <div
              id={AddVehicleSteps.VEHICLE_DETAILS}
              ref={createRefCallback(AddVehicleSteps.VEHICLE_DETAILS)}
              className="mt-3"
            >
              <AddBasicCarDataForm
                form={form}
                makeOptions={makeOptions}
                modelOptions={modelOptions}
                onMakeChange={handleMakeChange}
                validationErrors={validationErrors}
                onValidate={() =>
                  handleValidateSection(validateVehicleBasicData, 'VEHICLE_DETAILS')
                }
              />
            </div>
            <div
              id={AddVehicleSteps.SPECIFICATION}
              ref={createRefCallback(AddVehicleSteps.SPECIFICATION)}
              className="mt-4"
            >
              <AddFeaturesForm
                form={form}
                allVehicleFeatures={allVehicleFeatures}
                validationErrors={validationErrors}
                onValidate={() =>
                  handleValidateSection(validateVehicleFeaturesLocal, 'SPECIFICATION')
                }
              />
            </div>
            <div
              id={AddVehicleSteps.DESCRIPTION}
              ref={createRefCallback(AddVehicleSteps.DESCRIPTION)}
            >
              <AddDescriptionPage
                form={form}
                saveDraftRef={saveDraftRef}
                showSuccessAlertRef={showSuccessAlertRef}
                validationErrors={validationErrors}
                onValidate={() =>
                  handleValidateSection(validateVehicleDescription, 'DESCRIPTION')
                }
              />
            </div>
            <div
              id={AddVehicleSteps.PHOTOS}
              ref={createRefCallback(AddVehicleSteps.PHOTOS)}
              className="mt-4"
            >
              <AddPhotoForm
                form={form}
                onPhotoDrop={handlePhotoDrop}
                onPhotoDelete={handlePhotoDelete}
                onPhotoReorder={handlePhotoReorder}
                validationErrors={validationErrors}
              />
            </div>
            <div
              className="mt-4"
              id={AddVehicleSteps.VIDEOS}
              ref={createRefCallback(AddVehicleSteps.VIDEOS)}
            >
              <AddVideoForm
                onUpload={handleUploadVideo}
                onDelete={handleRemoveVideoClick}
                videos={videos}
                isLoading={isVideoLoading}
                validationError={validationError}
              />
            </div>
            <div
              id={AddVehicleSteps.CONDITION_INSPECTION}
              ref={createRefCallback(AddVehicleSteps.CONDITION_INSPECTION)}
            >
              <ConditionInspectionForm
                form={form}
                validationErrors={validationErrors}
                // onValidate={() =>
                //   handleValidateSection(validateConditionInspection, 'CONDITION_INSPECTION')
                // }
              />
            </div>
            <div id={AddVehicleSteps.TYRES} ref={createRefCallback(AddVehicleSteps.TYRES)}>
              <TyreTreadDepths
                form={form}
                validationErrors={validationErrors}
                onValidate={() => handleValidateSection(validateVehicleTyres, 'TYRES')}
              />
            </div>
            <div
              id={AddVehicleSteps.SERVICE_HISTORY}
              ref={createRefCallback(AddVehicleSteps.SERVICE_HISTORY)}
            >
              <ServiceHistoryPage
                form={form}
                validationErrors={validationErrors}
                onValidate={() =>
                  handleValidateSection(validateServiceHistoryLocal, 'SERVICE_HISTORY')
                }
              />
            </div>
            <div
              id={AddVehicleSteps.LAUNCH_VEHICLE}
              ref={createRefCallback(AddVehicleSteps.LAUNCH_VEHICLE)}
            >
              <LaunchVehicle
                form={form}
                organizationLocations={organizationLocations}
                organizationGroup={organizationGroup}
                setIsScheduledAuction={setIsScheduledAuction}
                validationErrors={validationErrors}
                onValidate={() => handleValidateSection(validateLaunchData, 'LAUNCH_VEHICLE')}
              />
            </div>
          </fieldset>
        </div>
      </div>
    );
  },
);

AddVehicleForm.propTypes = {
  form: PropTypes.shape({
    values: PropTypes.object.isRequired,
    initialValues: PropTypes.object,
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
  }).isRequired,
  setSavingDraft: PropTypes.func.isRequired,
  setPublishing: PropTypes.func.isRequired,
  saveDraftRef: PropTypes.shape({
    current: PropTypes.func,
  }).isRequired,
  publishRef: PropTypes.shape({
    current: PropTypes.func,
  }).isRequired,
};

export default AddVehicleForm;
